import {
  type AccountInfo,
  type IPublicClientApplication,
  InteractionRequiredAuthError,
  type SilentRequest,
} from '@azure/msal-browser';

// Refresh the ID token when it expires within this time (5 minutes)
export const ID_TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

const isIdTokenFresh = (exp: unknown): boolean =>
  typeof exp === 'number' &&
  exp * 1000 - Date.now() > ID_TOKEN_REFRESH_BUFFER_MS;

/**
 * Gets a valid ID token for the API.
 *
 * MSAL decides to refresh from the access token, not the ID token. The access
 * token can live up to ~30 minutes longer than the ID token, so MSAL can
 * return an expired ID token from its cache. When that happens, force a
 * refresh. If the session cannot be refreshed silently, send the user to
 * sign in again.
 */
export const acquireIdToken = async (
  instance: IPublicClientApplication,
  request: SilentRequest & { account: AccountInfo },
  options: { forceRefresh?: boolean } = {},
): Promise<string> => {
  try {
    let response = await instance.acquireTokenSilent({
      ...request,
      forceRefresh: options.forceRefresh ?? false,
    });

    const claims = response.idTokenClaims;
    const exp = 'exp' in claims ? claims.exp : undefined;

    if (!options.forceRefresh && !isIdTokenFresh(exp)) {
      response = await instance.acquireTokenSilent({
        ...request,
        forceRefresh: true,
      });
    }

    return response.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await instance.acquireTokenRedirect(request);
    }
    throw error;
  }
};
